import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  CircularProgress,
  Divider,
  InputAdornment,
  IconButton,
  useTheme,
  useMediaQuery,
  alpha,
  Paper
} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import useAuth from '../hooks/useAuth';
import useNotification from '../hooks/useNotification';
import { APP_NAME } from '../App';
import { Box, Typography, Button, TextField } from '@mui/material';

// Using Ailyce's login image
const loginImageUrl = 'https://infanion.lienion.com/Loginimg.png';
const logoUrl = '/logo-gray.svg';

/**
 * Login Page
 * Allows users to authenticate with their credentials
 */
const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  
  const { login, loading, isAuthenticated, isInitialized } = useAuth();
  const { showNotification } = useNotification();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Get the redirect path from the location state, or default to projects
  const from = location.state?.from?.pathname || '/projects';

  // Redirect to projects if already authenticated
  useEffect(() => {
    if (isAuthenticated && !loading) {
      navigate('/projects', { replace: true });
    }
  }, [isAuthenticated, loading, navigate]);

  // Avoid flashing the login form while auth is initializing or when the
  // user is already authenticated (the effect above will redirect).
  if (!isInitialized || isAuthenticated) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f5f5f5',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    // Basic validation
    if (!username || !password) {
      setError('Please enter both username and password');
      return;
    }
    
    try {
      await login(username, password);
      
      // Show success notification
      showNotification({
        message: 'Login successful!',
        type: 'success'
      });
      
      // Redirect to the page they tried to visit or projects
      navigate(from, { replace: true });
    } catch (error) {
      console.error('Login error:', error);
      setError(error.message || 'Invalid username or password');
    }
  };

  const toggleShowPassword = () => {
    setShowPassword(!showPassword);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        backgroundColor: '#f5f5f5',
      }}
    >
      {/* Left section - Brand */}
      {!isMobile && (
        <Box
          sx={{
            flex: '0 0 58.33%', // col-7 (7/12 = 58.33%)
            backgroundImage: `url(${loginImageUrl})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <div className="login_bg_overlay__PPsaZ"></div>
          <Box sx={{ position: 'relative', textAlign: 'center', color: 'white', px: 6, zIndex: 1 }}>
            {/* Welcome text removed as requested */}
          </Box>
        </Box>
      )}

      {/* Right section - Login form */}
      <Box
        sx={{
          flex: isMobile ? '1' : '0 0 41.67%', // col-5 (5/12 = 41.67%)
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          p: { xs: 3, md: 4 },
          backgroundColor: '#fff',
        }}
      >
        <Paper
          elevation={0}
          sx={{
            width: isMobile ? '100%' : '420px',
            p: 4,
            borderRadius: '4px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            border: 'none',
          }}
        >
          {/* Logo */}
          <Box sx={{ mb: 4, textAlign: 'center' }}>
            <img 
              src={logoUrl} 
              alt="Tremel AI Logo" 
              style={{ height: '80px', marginBottom: '16px' }}
              onError={(e) => {
                e.target.style.display = 'none';
                // Show text fallback if image fails to load
                document.getElementById('logo-text').style.display = 'block';
              }}
            />
            <Typography 
              id="logo-text" 
              variant="h4" 
              component="div" 
              fontWeight="bold" 
              color={theme.palette.primary.main} 
              sx={{ display: 'none' }}
            >
              {APP_NAME}
            </Typography>
          </Box>
          
          <Typography 
            variant="body1" 
            component="h2" 
            gutterBottom 
            fontWeight="medium"
            sx={{ 
              fontSize: 'var(--font-size-16px)', 
              mb: 3, 
              color: 'var(--color-blue) !important' 
            }}
          >
            Sign in by credentials
          </Typography>
          
          {error && (
            <Box 
              sx={{ 
                mb: 3,
                p: 2,
                width: '100%',
                bgcolor: alpha(theme.palette.error.main, 0.08),
                color: 'error.dark',
                borderRadius: 1,
              }}
            >
              <Typography variant="body2" color="inherit">
                {error}
              </Typography>
            </Box>
          )}
          
          {/* Login Form */}
          <Box component="form" onSubmit={handleSubmit} noValidate sx={{ width: '100%', mt: 2, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            {/* Username Field */}
            <Box sx={{ mb: 3, width: '304px' }}>
              <Typography 
                component="label" 
                variant="body2" 
                sx={{ 
                  mb: 1,
                  display: 'block',
                  color: 'var(--color-blue) !important',
                  fontWeight: 500,
                  fontSize: '14px',
                }}
              >
                Username
              </Typography>
              <TextField
                fullWidth
                id="username"
                name="username"
                placeholder="Enter username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                variant="outlined"
                sx={{ 
                  width: '100%',
                  height: '45px',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '4px',
                    color: 'var(--color-blue) !important',
                    height: '45px',
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#475969',
                      borderWidth: '1px',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#475969',
                      borderWidth: '1px',
                      borderRadius: '4px',
                    },
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#4759694d',
                    border: '1px solid #4759694d',
                    borderRadius: '4px',
                  },
                  '& .MuiInputBase-input': {
                    padding: '12px 14px',
                    fontSize: '14px',
                    color: 'var(--color-blue) !important',
                  },
                  '& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus': {
                    WebkitBoxShadow: '0 0 0 1000px white inset',
                    transition: 'background-color 5000s ease-in-out 0s',
                    WebkitTextFillColor: 'var(--color-blue)',
                    height: '45px'
                  },
                }}
              />
            </Box>
            
            {/* Password Field */}
            <Box sx={{ mb: 5, width: '304px' }}>
              <Typography 
                component="label" 
                variant="body2" 
                sx={{ 
                  mb: 1,
                  display: 'block',
                  color: 'var(--color-blue) !important',
                  fontWeight: 500,
                  fontSize: '14px',
                }}
              >
                Password
              </Typography>
              <Box sx={{ position: 'relative' }}>
                <TextField
                  fullWidth
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  placeholder="Enter password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  variant="outlined"
                  sx={{ 
                    width: '100%',
                    height: '45px',
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '4px',
                      color: 'var(--color-blue) !important',
                      height: '45px',
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#475969',
                        borderWidth: '1px',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#475969',
                        borderWidth: '1px',
                        borderRadius: '4px',
                      },
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#4759694d',
                      border: '1px solid #4759694d',
                      borderRadius: '4px',
                    },
                    '& .MuiInputBase-input': {
                      padding: '12px 14px',
                      fontSize: '14px',
                      color: 'var(--color-blue) !important',
                    },
                    '& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus': {
                      WebkitBoxShadow: '0 0 0 1000px white inset',
                      transition: 'background-color 5000s ease-in-out 0s',
                      WebkitTextFillColor: 'var(--color-blue)',
                      height: '45px'
                    },
                  }}
                />
                <Box 
                  sx={{ 
                    position: 'absolute', 
                    right: 6, 
                    top: '50%', 
                    transform: 'translateY(-50%)',
                    color: '#a2abb3',
                    cursor: 'pointer',
                    display: 'flex',
                  }}
                  onClick={toggleShowPassword}
                  
                >
                  {showPassword ? <VisibilityOutlinedIcon fontSize="small" /> : <VisibilityOffOutlinedIcon fontSize="small" />}
                </Box>
              </Box>
            </Box>
            
            {/* Login Button */}
            <Box sx={{ width: '304px', mb: 2 }}>
              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={loading}
                className="login_LoginButton__IziEy"
                sx={{ 
                  py: 1.2,
                  height: '40px',
                  bgcolor: '#485869', 
                  color: '#ffffff',
                  fontFamily: '"Sofia Pro Regular", "Sofia Pro", sans-serif',
                  fontSize: 'var(--font-size-20px)',
                  lineHeight: 'var(--line-height-30px)',
                  fontWeight: 400,
                  border: '1px solid #485869',
                  borderRadius: '5px',
                  textTransform: 'capitalize',
                  '&:hover': {
                    bgcolor: '#3A4754',
                  },
                  boxShadow: 'none',
                  mb: 2,
                }}
              >
                {loading ? <CircularProgress size={20} color="inherit" /> : 'Login'}
              </Button>
            </Box>
            
            {/* Forgot Password */}
            <Box sx={{ width: '304px', mb: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                <Typography
                  variant="body2"
                  component="a"
                  onClick={() => navigate('/forgot-password')}
                  sx={{
                    cursor: 'pointer',
                    color: 'var(--color-blue) !important',
                    textDecoration: 'none',
                    fontWeight: 500,
                    '&:hover': {
                      textDecoration: 'underline',
                    }
                  }}
                >
                  Forgot password?
                </Typography>
              </Box>
            </Box>
          </Box>
        </Paper>
        
      </Box>
    </Box>
  );
};

export default Login;